// src/components/Layout/TopToolbar/TopToolbar.tsx
import React from 'react';
import { CircleHelp, PanelLeft, PanelRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FileActions } from '@components/Toolbar/FileActions/FileActions';
import { ViewControls } from '@components/Toolbar/ViewControls/ViewControls';
import { ThemeToggle } from '@components/Toolbar/ThemeToggle/ThemeToggle';
import { UserMenu } from '@components/Toolbar/UserMenu';

interface TopToolbarProps {
  onToggleLeftSidebar: () => void;
  onToggleRightSidebar: () => void;
  isLeftSidebarOpen: boolean;
  isRightSidebarOpen: boolean;
  isReadOnly?: boolean;
  onOpenHelp: () => void;
}

export const TopToolbar: React.FC<TopToolbarProps> = ({
  onToggleLeftSidebar,
  onToggleRightSidebar,
  isLeftSidebarOpen,
  isRightSidebarOpen,
  isReadOnly = false,
  onOpenHelp
}) => {
  const navigate = useNavigate();

  return (
    <div className="relative z-20 flex h-14 items-center justify-between border-b border-gray-200 gradient-icon-card px-4 dark:border-slate-700">
      {/* Left Section - Branding + Sidebar Toggles + File Actions */}
      <div className="flex items-center gap-4">
        {/* Logo with enhanced styling */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="relative z-30 flex items-center justify-center transition hover:opacity-80 cursor-pointer"
          title="Go to home"
          aria-label="Go to home"
        >
          <img
            src="/logo.svg"
            alt="Degre120"
            className="h-8 w-auto object-contain"
          />
        </button>

        {/* Only show editing controls when not in read-only mode */}
        {!isReadOnly && (
          <>
            {/* Divider */}
            <div className="h-6 w-px bg-blue-300/50 dark:bg-slate-600" />

            {/* Sidebar Toggle Buttons */}
            <div className="flex items-center gap-2">
              {/* Left Sidebar Toggle */}
              <button
                type="button"
                onClick={onToggleLeftSidebar}
                className={`flex h-8 w-8 items-center justify-center rounded-md border transition-all ${
                  isLeftSidebarOpen
                    ? 'border-blue-400 bg-blue-50 text-blue-600 shadow-sm dark:border-blue-500 dark:bg-blue-950/50 dark:text-blue-400'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:bg-slate-700'
                }`}
                title="Toggle Icon Library (Ctrl+B)"
                aria-label="Toggle component library"
                aria-pressed={isLeftSidebarOpen}
              >
                <PanelLeft size={16} />
              </button>

              {/* Right Sidebar Toggle */}
              <button
                type="button"
                onClick={onToggleRightSidebar}
                className={`flex h-8 w-8 items-center justify-center rounded-md border transition-all ${
                  isRightSidebarOpen
                    ? 'border-blue-400 bg-blue-50 text-blue-600 shadow-sm dark:border-blue-500 dark:bg-blue-950/50 dark:text-blue-400'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:bg-slate-700'
                }`}
                title="Toggle Properties & Layers (Ctrl+Shift+B)"
                aria-label="Toggle properties and layers"
                aria-pressed={isRightSidebarOpen}
              >
                <PanelRight size={16} />
              </button>
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-blue-300/50 dark:bg-slate-600" />

            {/* File Actions */}
            <FileActions />
          </>
        )}
      </div>

      {/* Right Section - View Controls & Theme */}
      <div className="flex items-center gap-4">
        {/* View Controls (Grid, Zoom, Share - NO sidebar toggles) */}
        <ViewControls />

        <button
          type="button"
          onClick={onOpenHelp}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-blue-700 dark:hover:bg-blue-950 dark:hover:text-blue-300"
          title="Help and keyboard shortcuts (?)"
          aria-label="Help and keyboard shortcuts"
        >
          <CircleHelp size={16} />
        </button>

        {/* Divider */}
        <div className="h-6 w-px bg-blue-300/50 dark:bg-slate-600" />

        <ThemeToggle />

        {/* Divider */}
        <div className="h-6 w-px bg-blue-300/50 dark:bg-slate-600" />

        {/* User Menu */}
        <UserMenu />
      </div>
    </div>
  );
};
